import { type OnChangeFn, type SortingState } from "@tanstack/react-table";
import { useMemo } from "react";
import { createSortByArray, isValidSortField } from "./utils";

/**
 * A hook for managing sorting state in data tables with URL persistence via TanStack Router
 * and localStorage persistence for user preferences.
 * Validates URL sort fields against valid column IDs to prevent invalid state.
 *
 * Priority order for sort state:
 * 1. URL search params (highest priority - for sharing/bookmarking)
 * 2. localStorage preference (restored when URL has no sort params)
 * 3. Initial sort (fallback default)
 *
 * @template TColumnId - The type of column ID that will be used for sorting
 * @param params - Configuration object
 * @param params.initialSort - The initial/default sorting configuration (used as fallback)
 * @param params.validFields - Array of valid field values (for runtime validation)
 * @param params.sortParams - Sort parameters from TanStack Router URL (sortField, sortDesc)
 * @param params.onSortParamsChange - Callback to update sort parameters in URL
 * @param params.localStorageSort - (Optional) Sort preference stored in localStorage (persisted user preference)
 * @param params.onLocalStorageSortChange - (Optional) Callback to update localStorage sort preference
 * @returns Object containing:
 * - sorting: TanStack Table sorting state
 * - onSortingChange: Function to update sorting state (updates both URL and localStorage)
 * - sortBy: API-ready sort parameter for GraphQL queries
 *
 * @example
 * ```tsx
 * const validFields = ["PLANNED_SHIPPING_DATE", "NAME", "STATUS"] as const;
 * type SortingField = (typeof validFields)[number];
 *
 * const { sortField, sortDesc } = routeApi.useSearch();
 * const navigate = routeApi.useNavigate();
 * const [sortPref, setSortPref] = useSortPreference("bookingsTable");
 *
 * const { sorting, onSortingChange, sortBy } = useUrlTableSorting<SortingField>({
 *   initialSort: { id: "PLANNED_SHIPPING_DATE", desc: true },
 *   validFields,
 *   sortParams: { sortField, sortDesc },
 *   localStorageSortPreference: sortPref,
 *   onSortParamsChange: (field, desc) => {
 *     void navigate({ search: (prev) => ({ ...prev, sortField: field, sortDesc: desc }) });
 *   },
 *   onSortPreferenceChange: setSortPref,
 * });
 *
 * // Use with TanStack Table
 * <DataTable
 *   sortConfig={{ sorting, onSortingChange }}
 *   {...otherProps}
 * />
 *
 * // Use with GraphQL query
 * const variables = {
 *   sortBy,
 *   ...otherVariables
 * };
 * ```
 */
export function useUrlTableSorting<TColumnId extends string>(params: {
  initialSort: { id: TColumnId; desc: boolean };
  validFields: readonly TColumnId[];
  sortParams: { sortField?: string; sortDesc?: boolean };
  onSortParamsChange: (field: TColumnId, desc: boolean) => void;
  localStorageSort?: { sortField?: string; sortDesc?: boolean };
  onLocalStorageSortChange?: (preference: { sortField?: string; sortDesc?: boolean }) => void;
}) {
  const { initialSort, validFields, sortParams, localStorageSort, onSortParamsChange, onLocalStorageSortChange } =
    params;

  // Priority: URL params > localStorage preference > initial sort
  const sortFieldFromUrl = sortParams.sortField;
  const sortDescFromUrl = sortParams.sortDesc;

  // Use URL if present, otherwise use localStorage preference, otherwise use initial
  const sortFieldToValidate = sortFieldFromUrl ?? localStorageSort?.sortField;
  const validatedSortField = isValidSortField(sortFieldToValidate, validFields) ? sortFieldToValidate : initialSort.id;

  const sortDesc = sortDescFromUrl ?? localStorageSort?.sortDesc ?? initialSort.desc;

  const sorting: SortingState = useMemo(
    () => [{ id: validatedSortField, desc: sortDesc }],
    [validatedSortField, sortDesc],
  );

  const handleSortingChange: OnChangeFn<SortingState> = (updaterOrValue) => {
    const newSorting = typeof updaterOrValue === "function" ? updaterOrValue(sorting) : updaterOrValue;

    // If sorting is cleared (empty array), reset to initial sort
    const sortToApply = newSorting[0] ?? initialSort;
    const sortField = sortToApply.id as TColumnId;
    const sortDesc = sortToApply.desc;

    // Update URL sort params
    onSortParamsChange(sortField, sortDesc);

    // Update localStorage preference if callback provided
    if (onLocalStorageSortChange) {
      onLocalStorageSortChange({ sortField, sortDesc });
    }
  };

  const sortBy = useMemo(
    () => createSortByArray<TColumnId>(sorting, { id: initialSort.id, desc: initialSort.desc }),
    [sorting, initialSort.id, initialSort.desc],
  );

  return {
    sorting,
    onSortingChange: handleSortingChange,
    sortBy,
  };
}
